import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

const deserializeBigIntLike = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  if (typeof value === 'bigint') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

const deserializeNullableBigIntLike = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null
  }

  return deserializeBigIntLike(value)
}

export enum SaleCommissionTargetType {
  PACKAGE = 'PACKAGE',
  PRODUCT = 'PRODUCT',
}

export enum CommissionRateType {
  PERCENT = 'PERCENT',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

export enum SaleCommissionSaleSource {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
  ALL = 'ALL',
}

@Serializable()
export class SaleCommissionRuleRoleEntity extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class SaleCommissionConditionEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('orderIndex')
  orderIndex!: number

  @JsonProperty({
    path: 'amountFrom',
    deserialize: deserializeBigIntLike,
  })
  amountFrom!: number

  @JsonProperty({
    path: 'amountTo',
    deserialize: deserializeNullableBigIntLike,
  })
  amountTo!: number | null

  @JsonProperty('commissionType')
  commissionType!: CommissionRateType

  @JsonProperty({
    path: 'rate',
    deserialize: deserializeBigIntLike,
  })
  rate!: number
}

@Serializable()
export class SaleCommissionRuleTargetEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('package')
  package?: { id: number; name: string } | null

  @JsonProperty('product')
  product?: { id: string; title: string } | null
}

@Serializable()
export class SaleCommissionRuleEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('targetType')
  targetType!: SaleCommissionTargetType

  @JsonProperty('saleSource')
  saleSource!: SaleCommissionSaleSource

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('includeJoiningFee')
  includeJoiningFee!: boolean

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty({
    path: 'roles',
    type: SaleCommissionRuleRoleEntity,
    elementType: SaleCommissionRuleRoleEntity,
  })
  roles!: SaleCommissionRuleRoleEntity[]

  @JsonProperty({
    path: 'conditions',
    type: SaleCommissionConditionEntity,
    elementType: SaleCommissionConditionEntity,
  })
  conditions!: SaleCommissionConditionEntity[]

  @JsonProperty({
    path: 'targets',
    type: SaleCommissionRuleTargetEntity,
    elementType: SaleCommissionRuleTargetEntity,
  })
  targets!: SaleCommissionRuleTargetEntity[]
}

@Serializable()
export class SaleCommissionRuleDetailEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('targetType')
  targetType!: SaleCommissionTargetType

  @JsonProperty('saleSource')
  saleSource!: SaleCommissionSaleSource

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('includeJoiningFee')
  includeJoiningFee!: boolean

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty({
    path: 'targets',
    type: SaleCommissionRuleTargetEntity,
    elementType: SaleCommissionRuleTargetEntity,
  })
  targets!: SaleCommissionRuleTargetEntity[]

  @JsonProperty({
    path: 'roles',
    type: SaleCommissionRuleRoleEntity,
    elementType: SaleCommissionRuleRoleEntity,
  })
  roles!: SaleCommissionRuleRoleEntity[]

  @JsonProperty({
    path: 'conditions',
    type: SaleCommissionConditionEntity,
    elementType: SaleCommissionConditionEntity,
  })
  conditions!: SaleCommissionConditionEntity[]
}

@Serializable()
export class CreateSaleCommissionConditionDto extends Fromable {
  @JsonProperty('amountFrom')
  amountFrom!: number

  @JsonProperty('amountTo')
  amountTo?: number | null

  @JsonProperty('commissionType')
  commissionType!: CommissionRateType

  @JsonProperty('rate')
  rate!: number
}

@Serializable()
export class CreateSaleCommissionRuleDto extends Fromable {
  @JsonProperty('name')
  name!: string

  @JsonProperty('targetType')
  targetType!: SaleCommissionTargetType

  @JsonProperty('saleSource')
  saleSource?: SaleCommissionSaleSource

  @JsonProperty('packageIds')
  packageIds?: number[]

  @JsonProperty('productIds')
  productIds?: string[]

  @JsonProperty('includeJoiningFee')
  includeJoiningFee?: boolean

  @JsonProperty('roleIds')
  roleIds!: number[]

  @JsonProperty({
    path: 'conditions',
    type: CreateSaleCommissionConditionDto,
    elementType: CreateSaleCommissionConditionDto,
  })
  conditions!: CreateSaleCommissionConditionDto[]
}

@Serializable()
export class UpdateSaleCommissionRuleDto extends Fromable {
  @JsonProperty('name')
  name!: string

  @JsonProperty('saleSource')
  saleSource?: SaleCommissionSaleSource

  @JsonProperty('packageIds')
  packageIds?: number[]

  @JsonProperty('productIds')
  productIds?: string[]

  @JsonProperty('includeJoiningFee')
  includeJoiningFee?: boolean

  @JsonProperty('roleIds')
  roleIds!: number[]

  @JsonProperty({
    path: 'conditions',
    type: CreateSaleCommissionConditionDto,
    elementType: CreateSaleCommissionConditionDto,
  })
  conditions!: CreateSaleCommissionConditionDto[]
}

@Serializable()
export class UpdateSaleCommissionStatusDto extends Fromable {
  @JsonProperty('isActive')
  isActive!: boolean
}
